import { CardData, CardType, FirebaseDate, UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { rephraseQuestionAndAnswer, updateCardById } from "../models/CardModel";
import { trackEvent } from "./helper-methods";

/**
 * Check if a question should be rephrased based on the conditions
 */
export const shouldRephraseQuestion = (
  card: CardData,
  userId: string,
  user?: UserData
): {
  shouldRephrase: boolean;
  reason?: string;
} => {
  // Check if user has disabled question rephrasing
  if (user && user?.user_settings?.should_rephrase_questions === false) {
    return { shouldRephrase: false, reason: "User disabled question rephrasing" };
  }

  if (card.question_generation_template_id === "table_mcq") {
    return { shouldRephrase: false, reason: "Table questions require their original chart and answer" };
  }

  // Only rephrase MCQ & True/False questions. case question not supported.
  if (card.card_type !== CardType.RegularMultipleChoiceQuestion && card.card_type !== CardType.TrueOrFalseQuestion) {
    return { shouldRephrase: false, reason: "Only MCQ & True/False questions are supported for rephrasing" };
  }

  // For MCQ questions, check if we have exactly 3 distractor answers
  if (
    card.card_type === CardType.RegularMultipleChoiceQuestion &&
    (!card.distractor_answers_for_multiple_choice_question ||
      card.distractor_answers_for_multiple_choice_question.length !== 3)
  ) {
    return { shouldRephrase: false, reason: "MCQ must have exactly 3 distractor answers" };
  }

  // Check if question is unanswered in current session
  if (card.selectedAnswerText) {
    return { shouldRephrase: false, reason: "Already answered in current session" };
  }

  const rephrasedVersions = card.rephrased_question_versions || [];
  const hasUserAnsweredOriginal = card.understanding_ratings_for_card_by_user?.[userId] != null;

  // Case 1: No rephrased versions exist
  if (rephrasedVersions.length === 0) {
    if (hasUserAnsweredOriginal) {
      return { shouldRephrase: true, reason: "User has answered original, creating first rephrase" };
    }
    return { shouldRephrase: false, reason: "Original not yet answered" };
  }

  // Case 2: One rephrased version exists
  if (rephrasedVersions.length === 1) {
    const firstrephrasedQuestionLastAnsweredDate = rephrasedVersions[0]?.date_last_answered_by_user?.[userId] != null;
    if (firstrephrasedQuestionLastAnsweredDate) {
      return { shouldRephrase: true, reason: "First rephrase answered, creating second" };
    }
    return { shouldRephrase: false, reason: "First rephrase not yet answered" };
  }

  // Case 3: Two or more rephrased versions exist - don't create more
  return { shouldRephrase: false, reason: "Maximum rephrases reached" };
};

/**
 * Convert Firebase date to Date object
 */
const convertFirebaseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  if (typeof dateValue === "object" && dateValue !== null && "seconds" in dateValue && "nanoseconds" in dateValue) {
    return new Date((dateValue as FirebaseDate).seconds * 1000);
  }
  return new Date(dateValue as Date);
};

/**
 * Get the last answered date for a question version
 */
export const getLastAnsweredDate = (
  card: CardData,
  userId: string,
  versionIndex: number // -1 for original, 0+ for rephrased versions
): Date | null => {
  if (versionIndex === -1) {
    // For original question, check if most_recent_date_rated corresponds to original or rephrased
    const cardAnsweredMetadata = card.understanding_ratings_for_card_by_user?.[userId];
    if (!cardAnsweredMetadata?.most_recent_date_rated) {
      return null;
    }

    const mostRecentDate = convertFirebaseDate(cardAnsweredMetadata.most_recent_date_rated);
    if (!mostRecentDate) return null;

    // Check if this most_recent_date_rated matches any rephrased version's date
    const rephrasedVersions = card.rephrased_question_versions || [];
    for (let i = 0; i < rephrasedVersions.length; i++) {
      const rephrasedDate = rephrasedVersions[i]?.date_last_answered_by_user?.[userId];
      if (rephrasedDate) {
        const convertedRephrasedDate = convertFirebaseDate(rephrasedDate);
        if (convertedRephrasedDate) {
          // If dates are very close (within 2 seconds), most_recent_date_rated is for the rephrased version
          const timeDiff = Math.abs(mostRecentDate.getTime() - convertedRephrasedDate.getTime());
          if (timeDiff < 2000) {
            return null; // This means the original wasn't the most recent
          }
        }
      }
    }

    // If most_recent_date_rated doesn't match any rephrased version, it's for the original
    return mostRecentDate;
  } else {
    // Rephrased question - return the date for this specific version
    const rephrasedVersion = card.rephrased_question_versions?.[versionIndex];
    const dateAnswered = rephrasedVersion?.date_last_answered_by_user?.[userId];
    return convertFirebaseDate(dateAnswered);
  }
};

/**
 * Update card data with the version fields being used
 * Always sets values - uses original question/answer as defaults
 */
export const updateCardWithVersionBeingUsed = (
  card: CardData,
  versionData: {
    versionIndex: number;
    question: string;
    answer: string;
    distractorAnswers?: string[];
  }
): CardData => {
  return {
    ...card,
    questionVersionBeingUsed: versionData.question || card.question,
    answerVersionBeingUsed: versionData.answer || card.answer,
    distractorAnswersVersionBeingUsed:
      versionData.distractorAnswers || card.distractor_answers_for_multiple_choice_question || [],
    rephrasedQuestionVersionIndexBeingUsed: versionData.versionIndex,
  };
};

/**
 * Preserve the exact question version already shown, or populate every runtime
 * version field from the same resolver used when a card becomes current.
 */
export const ensureCardHasVersionBeingUsed = (card: CardData, user?: UserData): CardData => {
  const hasCompleteVersion =
    typeof card.questionVersionBeingUsed === "string" &&
    card.questionVersionBeingUsed.length > 0 &&
    typeof card.answerVersionBeingUsed === "string" &&
    card.answerVersionBeingUsed.length > 0 &&
    Array.isArray(card.distractorAnswersVersionBeingUsed) &&
    typeof card.rephrasedQuestionVersionIndexBeingUsed === "number";

  if (hasCompleteVersion) return card;
  return updateCardWithVersionBeingUsed(card, getQuestionVersionToDisplay(card, user));
};

/**
 * Determine which version of the question to display
 */
export const getQuestionVersionToDisplay = (
  card: CardData,
  user?: UserData
): {
  versionIndex: number; // -1 for original, 0+ for rephrased
  question: string;
  answer: string;
  distractorAnswers?: string[];
  isBeingRephrased: boolean;
} => {
  if (card.question_generation_template_id === "table_mcq") {
    return {
      versionIndex: -1,
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: false,
    };
  }
  // Prefer user?.id for all logic. If missing, always use original question
  const resolvedUserId = user?.id;
  if (!resolvedUserId) {
    return {
      versionIndex: -1,
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: false,
    };
  }

  // If user has disabled question rephrasing, always show original
  if (user && user?.user_settings?.should_rephrase_questions === false) {
    return {
      versionIndex: -1,
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: false,
    };
  }
  // IMPORTANT: If question has been answered in current session, preserve the version that was shown
  // This prevents the question text from changing after the user selects an answer
  if (card.selectedAnswerText) {
    // Check if we have a stored version index from when the question was displayed
    // If not, default to original question
    const displayedVersionIndex = card.displayedVersionIndex ?? -1;

    if (displayedVersionIndex === -1) {
      // Show original version
      return {
        versionIndex: -1,
        question: card.question,
        answer: card.answer,
        distractorAnswers: card.distractor_answers_for_multiple_choice_question,
        isBeingRephrased: false,
      };
    } else {
      // Show the rephrased version that was displayed when user answered
      const rephrasedVersions = card.rephrased_question_versions || [];
      const rephrasedVersion = rephrasedVersions[displayedVersionIndex];
      if (rephrasedVersion) {
        return {
          versionIndex: displayedVersionIndex,
          question: rephrasedVersion.question,
          answer: rephrasedVersion.answer,
          distractorAnswers: rephrasedVersion.distractor_answers_for_multiple_choice_question,
          isBeingRephrased: false,
        };
      }
      // Fallback to original if rephrased version not found
      return {
        versionIndex: -1,
        question: card.question,
        answer: card.answer,
        distractorAnswers: card.distractor_answers_for_multiple_choice_question,
        isBeingRephrased: false,
      };
    }
  }

  // If currently being rephrased, show original with loading state
  if (card.is_question_being_rephrased) {
    return {
      versionIndex: -1, // -1 means we use the original question
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: true,
    };
  }

  const rephrasedVersions = card.rephrased_question_versions || [];

  // If no rephrased versions, use original
  if (rephrasedVersions.length === 0) {
    return {
      versionIndex: -1, // -1 means we use the original question
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: false,
    };
  }

  // If only one rephrased version exists
  if (rephrasedVersions.length === 1) {
    const originalQuestionLastAnsweredDate = getLastAnsweredDate(card, resolvedUserId, -1) !== null;
    const rephrasedQuestionLastAnsweredDate = getLastAnsweredDate(card, resolvedUserId, 0) !== null;

    // If neither answered, show original
    if (!originalQuestionLastAnsweredDate && !rephrasedQuestionLastAnsweredDate) {
      return {
        versionIndex: -1, // -1 means we use the original question
        question: card.question,
        answer: card.answer,
        distractorAnswers: card.distractor_answers_for_multiple_choice_question,
        isBeingRephrased: false,
      };
    }

    // If only original answered, show rephrased
    if (originalQuestionLastAnsweredDate && !rephrasedQuestionLastAnsweredDate) {
      return {
        versionIndex: 0,
        question: rephrasedVersions[0].question,
        answer: rephrasedVersions[0].answer,
        distractorAnswers: rephrasedVersions[0].distractor_answers_for_multiple_choice_question,
        isBeingRephrased: false,
      };
    }

    // Both answered - show original (user will see rephrased after answering)
    return {
      versionIndex: -1, // -1 means we use the original question
      question: card.question,
      answer: card.answer,
      distractorAnswers: card.distractor_answers_for_multiple_choice_question,
      isBeingRephrased: false,
    };
  }

  // Two or more rephrased versions exist
  // Find the most recently answered version
  let mostRecentDate: Date | null = null;
  let mostRecentIndex = -1;

  // Check original
  const originalDate = getLastAnsweredDate(card, resolvedUserId, -1);
  if (originalDate) {
    mostRecentDate = originalDate;
    mostRecentIndex = -1;
  }

  // Check rephrased versions
  for (let i = 0; i < rephrasedVersions.length; i++) {
    const date = getLastAnsweredDate(card, resolvedUserId, i);
    if (date && (!mostRecentDate || date > mostRecentDate)) {
      mostRecentDate = date;
      mostRecentIndex = i;
    }
  }

  // Get all available versions (original + rephrased)
  const allVersions: Array<{ index: number; question: string; answer: string; distractors?: string[] }> = [
    {
      index: -1,
      question: card.question,
      answer: card.answer,
      distractors: card.distractor_answers_for_multiple_choice_question,
    },
    ...rephrasedVersions.map((v, i) => ({
      index: i,
      question: v.question,
      answer: v.answer,
      distractors: v.distractor_answers_for_multiple_choice_question,
    })),
  ];

  // Filter out the most recently answered version
  const availableVersions = allVersions.filter((v) => v.index !== mostRecentIndex);

  // Randomly select from available versions
  const selectedVersion = availableVersions[Math.floor(Math.random() * availableVersions.length)];

  return {
    versionIndex: selectedVersion.index,
    question: selectedVersion.question,
    answer: selectedVersion.answer,
    distractorAnswers: selectedVersion.distractors,
    isBeingRephrased: false,
  };
};

/**
 * Update card with rephrased question data
 */
export const updateCardWithRephrasedQuestion = async (
  cardId: string,
  rephrasedData: {
    question: string;
    answer: string;
    distractor_answers: string[];
  },
  existingRephrasedVersions: Array<any> = []
): Promise<boolean> => {
  try {
    const newRephrasedVersion = {
      question: rephrasedData.question,
      answer: rephrasedData.answer,
      distractor_answers_for_multiple_choice_question: rephrasedData.distractor_answers,
      date_last_answered_by_user: {},
    };

    // Append to existing rephrased versions array
    const updatedRephrasedVersions = [...existingRephrasedVersions, newRephrasedVersion];

    await updateCardById(cardId, {
      rephrased_question_versions: updatedRephrasedVersions,
      is_question_being_rephrased: false,
    });

    return true;
  } catch (error) {
    console.error("Error updating card with rephrased question:", error);
    trackEvent("Error: Failed to update card with rephrased question", {
      cardId,
      error: error.message,
    });
    return false;
  }
};

/**
 * Process rephrasing for a card
 */
export const processCardRephrasing = async (
  card: CardData,
  userId: string,
  updateCardFromCardsDataState?: (updatedCard: CardData) => void,
  user?: UserData
): Promise<void> => {
  const { shouldRephrase } = shouldRephraseQuestion(card, userId, user);

  if (!shouldRephrase) {
    return;
  }

  // Mark card as being rephrased in the database
  await updateCardById(card.id, { is_question_being_rephrased: true });

  // Update local state immediately to show loading state
  if (updateCardFromCardsDataState) {
    const updatedCard: CardData = {
      ...card,
      is_question_being_rephrased: true,
    };
    updateCardFromCardsDataState(updatedCard);
  }

  try {
    // Call rephrasing API
    const rephrasedData = await rephraseQuestionAndAnswer(
      card.id,
      card.question,
      card.answer,
      card.relevant_context_for_question || "",
      card?.distractor_answers_for_multiple_choice_question
    );

    if (rephrasedData) {
      // Create the new rephrased version
      const newRephrasedVersion = {
        question: rephrasedData.rephrased_question,
        answer: rephrasedData.rephrased_answer,
        distractor_answers_for_multiple_choice_question: rephrasedData.rephrased_distractor_answers,
        date_last_answered_by_user: {},
        created_at: new Date(),
      };

      // Update card with rephrased data
      const updatedRephrasedVersions = [...(card.rephrased_question_versions || []), newRephrasedVersion];

      await updateCardById(card.id, {
        rephrased_question_versions: updatedRephrasedVersions,
        is_question_being_rephrased: false,
      });

      // Update local state if update function is provided
      if (updateCardFromCardsDataState) {
        const updatedCard: CardData = {
          ...card,
          rephrased_question_versions: updatedRephrasedVersions,
          is_question_being_rephrased: false,
        };
        updateCardFromCardsDataState(updatedCard);
      }
    } else {
      // Failed to rephrase, reset flag
      await updateCardById(card.id, { is_question_being_rephrased: false });

      // Update local state if update function is provided
      if (updateCardFromCardsDataState) {
        const updatedCard: CardData = {
          ...card,
          is_question_being_rephrased: false,
        };
        updateCardFromCardsDataState(updatedCard);
      }
    }
  } catch (error) {
    console.error("Error processing card rephrasing:", error);
    // Reset flag on error
    await updateCardById(card.id, { is_question_being_rephrased: false });

    // Update local state if update function is provided
    if (updateCardFromCardsDataState) {
      const updatedCard: CardData = {
        ...card,
        is_question_being_rephrased: false,
      };
      updateCardFromCardsDataState(updatedCard);
    }
  }
};

/**
 * Process multiple cards for rephrasing (batch operation)
 * Returns the updated cards array with rephrasing data
 */
export const processMultipleCardsForRephrasing = async (
  cards: CardData[],
  userId: string,
  updateCardFromCardsDataState?: (updatedCard: CardData) => void,
  user?: UserData
): Promise<CardData[]> => {
  // Create a copy of the cards array to track updates
  const updatedCards = [...cards];

  // Create a map to quickly find and update cards by ID
  const cardMap = new Map(updatedCards.map((card, index) => [card.id, index]));

  // Custom update function that updates both the local array and calls the optional callback
  const updateCardInArray = (updatedCard: CardData) => {
    const cardIndex = cardMap.get(updatedCard.id);
    if (cardIndex !== undefined) {
      updatedCards[cardIndex] = updatedCard;
    }

    // Call the optional callback for real-time UI updates
    if (updateCardFromCardsDataState) {
      updateCardFromCardsDataState(updatedCard);
    }
  };

  // A slow rewrite holds only its own slot. Keep the existing limit across
  // the whole card operation, including its database writes and callbacks.
  const concurrencyLimit = 3;
  let nextCardIndex = 0;
  let hasUnhandledFailure = false;
  const processQueuedCards = async () => {
    while (!hasUnhandledFailure && nextCardIndex < cards.length) {
      const card = cards[nextCardIndex];
      nextCardIndex += 1;
      try {
        await processCardRephrasing(card, userId, updateCardInArray, user);
      } catch (error) {
        // Existing operations may finish, but do not start more queued work.
        hasUnhandledFailure = true;
        throw error;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrencyLimit, cards.length) }, processQueuedCards));

  return updatedCards;
};

/**
 * Update the date_last_answered_by_user for a rephrased question version
 */
export const updateRephrasedQuestionAnsweredDate = async (
  cardId: string,
  userId: string,
  versionIndex: number,
  rephrasedVersions: Array<any>
): Promise<void> => {
  if (versionIndex < 0 || versionIndex >= rephrasedVersions.length) {
    console.error("Invalid version index for updating rephrased question answered date");
    return;
  }

  try {
    // Update the specific version's date_last_answered_by_user
    const updatedVersions = [...rephrasedVersions];
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      date_last_answered_by_user: {
        ...updatedVersions[versionIndex].date_last_answered_by_user,
        [userId]: new Date(),
      },
    };

    await updateCardById(cardId, {
      rephrased_question_versions: updatedVersions,
    });
  } catch (error) {
    console.error("Error updating rephrased question answered date:", error);
  }
};
